import { Transition } from '@headlessui/react'
import cx from 'classnames'
import React, { useEffect, useState } from 'react'

type Props = {
  /** Child elements. */
  children: React.ReactNode
  /** External classnames applied to the list */
  className?: string
  /** Transition duration */
  duration?: 100 | 200 | 300 | 500
  /** Transition delay */
  delay?: 100 | 200 | 300 | 500
  /** Item height in PX. */
  height?: number
}

/**
 * Renders an item of an animated list.
 */
const AnimatedListItem: React.FC<
  React.LiHTMLAttributes<HTMLLIElement> & Props
> = ({ children, className, duration, height, delay, ...other }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShow(true)
    }, delay)

    return () => clearTimeout(timeout)
  }, [delay])

  return (
    <Transition
      show={show}
      enter={cx('transition-opacity duration-75', {
        'duration-100': duration === 100,
        'duration-200': duration === 200,
        'duration-300': duration === 300,
        'duration-500': duration === 500,
      })}
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave={cx('transition-opacity duration-75', {
        'duration-100': duration === 100,
        'duration-200': duration === 200,
        'duration-300': duration === 300,
        'duration-500': duration === 500,
      })}
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      as="div"
    >
      <li
        className={cx(
          `flex flex-col justify-center transition-all`,
          className,
          {
            'duration-100': duration === 100,
            'duration-200': duration === 200,
            'duration-300': duration === 300,
            'duration-500': duration === 500,
          },
        )}
        {...other}
        style={
          show
            ? { minHeight: `${height}px`, maxHeight: `${height}px` }
            : { maxHeight: '0px', minHeight: '0px' }
        }
      >
        {children}
      </li>
    </Transition>
  )
}

AnimatedListItem.defaultProps = {
  duration: 100,
  delay: 100,
  height: 50,
}

export default AnimatedListItem
